import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = '/api';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    document.title = 'Order Complete - Salt & Stone';
  }, []);

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (!sessionId) {
        setStatus('error');
        return;
      }

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(response.data);
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center px-6" data-testid="checkout-success-page">
      <div className="max-w-lg w-full text-center">
        {status === 'loading' && (
          <div className="animate-fade-in">
            <div className="spinner mx-auto mb-8"></div>
            <h1 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#F2F0E9] mb-4">
              Processing Payment
            </h1>
            <p className="text-[#F2F0E9]/60">
              Please wait while we confirm your order...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-8 border border-[#D4AF37] rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <span className="caption block mb-4">Order Confirmed</span>
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mb-6">
              Thank You
            </h1>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto mb-8"></div>
            <p className="text-[#F2F0E9]/70 mb-8 leading-relaxed">
              Your order has been successfully placed. We'll send you an email confirmation with your order details shortly.
            </p>
            
            {paymentData && (
              <div className="glass p-6 mb-8 text-left">
                <div className="flex justify-between mb-4">
                  <span className="text-[#F2F0E9]/60 text-sm">Amount Paid</span>
                  <span className="text-[#F2F0E9] font-medium">
                    €{(paymentData.amount_total / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#F2F0E9]/60 text-sm">Order Reference</span>
                  <span className="text-[#F2F0E9]/40 text-sm font-mono">
                    {sessionId?.slice(-8).toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
              Continue Shopping
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-8 border border-red-500/50 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9] mb-6">
              {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h1>
            <p className="text-[#F2F0E9]/70 mb-8 leading-relaxed">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t confirm your payment. If you were charged, please contact us.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/cart" className="btn-primary">
                Return to Cart
              </Link>
              <Link to="/contact" className="btn-secondary">
                Contact Support
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
